import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, FormControlLabel, Switch, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ComparisonValue,
  NoAccountsStub,
  WithLoadingSpinner,
} from "@components";
import { InvestmentChartDataItem } from "@hooks";
import { ChartWrapper } from "../../common";
import { useHighchartsOptions } from "./useChartOptions";

type Props = {
  chartData: {
    totalValuesWithDividends: InvestmentChartDataItem[];
    totalValues: InvestmentChartDataItem[];
    comparisonValues: InvestmentChartDataItem[];
  };
  comparedTicker?: ComparisonValue;
  showDividendsToggle?: boolean;
  isFetching?: boolean;
  isFetched?: boolean;
  height?: number;
};

export const PerformanceChart = ({
  chartData,
  comparedTicker,
  isFetching,
  isFetched,
  showDividendsToggle = true,
  height,
}: Props) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const [withDividends, setWithDividends] = useState(false);

  const chartOptions = useHighchartsOptions(
    chartData,
    theme,
    withDividends,
    comparedTicker,
    height
  );

  useEffect(() => {
    chartComponentRef.current?.chart?.reflow();
  }, [chartOptions]);

  return (
    <Box>
      <WithLoadingSpinner isLoading={Boolean(!isFetched || isFetching)} mb={1}>
        {isFetched && !isFetching && !chartData.totalValues.length ? (
          <NoAccountsStub height="330px" />
        ) : (
          <ChartWrapper>
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              ref={chartComponentRef}
            />
          </ChartWrapper>
        )}
      </WithLoadingSpinner>
      {showDividendsToggle && (
        <Box>
          <Box mb={3}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  size="medium"
                  checked={withDividends}
                  onChange={() => setWithDividends(!withDividends)}
                />
              }
              label={t("dashboardPage.dashboard.performance.includeDividends")}
              sx={{
                marginLeft: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: theme.typography.body1,
                  fontWeight: 600,
                },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
